import { Input } from "@/components/ui/input";
import { cleanRut, formatRut, validateRut } from "@/lib/rut";
import { cn } from "@/lib/utils";
import { type ComponentProps, useState } from "react";

type InputProps = ComponentProps<typeof Input>;

interface RutInputProps extends Omit<InputProps, "value" | "onChange"> {
  /**
   * RUT canónico (sin puntos, sin guión, DV mayúscula). Es lo que el padre
   * mantiene en su estado y lo que se envía al API.
   */
  value: string;
  /**
   * Emite el RUT canónico (limpio). El formateo visual queda dentro del
   * componente — el padre nunca ve el valor con puntos.
   */
  onChange: (clean: string) => void;
  /**
   * Si es true, valida el dígito verificador y muestra error inline cuando
   * el RUT es inválido. Solo marca error tras el primer blur o si el padre
   * fuerza `showError`. Default: false (back-compat).
   */
  validate?: boolean;
  /**
   * Mensaje de error a mostrar cuando el RUT es inválido.
   * Default: "RUT inválido".
   */
  errorMessage?: string;
  /**
   * Fuerza el error visible (útil para mostrarlo al intentar submit antes
   * de que el campo haya recibido blur).
   */
  showError?: boolean;
}

/**
 * Input de RUT chileno: muestra formato visual `12.345.678-9` mientras el
 * usuario escribe, pero el padre recibe siempre el canónico (`123456789`).
 *
 * Convención del proyecto: el API guarda canónico. Este componente es el
 * único responsable del formato visual en formularios.
 */
export function RutInput({
  value,
  onChange,
  validate = false,
  errorMessage = "RUT inválido",
  showError = false,
  onBlur,
  className,
  ...rest
}: RutInputProps) {
  const [touched, setTouched] = useState(false);

  const isInvalid = validate && value.length > 0 && !validateRut(value);
  const visible = isInvalid && (touched || showError);

  return (
    <div className="space-y-1">
      <Input
        {...rest}
        value={formatRut(value)}
        onChange={(e) => onChange(cleanRut(e.target.value))}
        onBlur={(e) => {
          setTouched(true);
          onBlur?.(e);
        }}
        inputMode="text"
        autoCapitalize="characters"
        aria-invalid={visible || undefined}
        className={cn(visible && "border-destructive", className)}
      />
      {visible && (
        <p className="text-sm text-destructive">{errorMessage}</p>
      )}
    </div>
  );
}
